import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useHotels } from "../components/Numbers";

/**
 * Request Records (SPEC §18). Pick a document, the tax year (quarter for
 * capital accounts) and the hotels. D4 (2026-10-27): documents are delivered
 * here by secure link — sign-in required, every download recorded — never as
 * an email attachment, because they carry taxpayer IDs.
 */

interface Catalog { documents: Array<{ key: string; label: string; cadence: string; available: boolean; note?: string }>; years: number[]; defaultYear: number }
interface Item { id: string; hotelId: string; hotelName: string; ready: boolean; fileName: string | null; uploadedAt: string | null; downloadedAt: string | null }
interface Req { id: string; label: string; periodLabel: string; note: string | null; createdAt: string; status: "ready" | "partly_ready" | "requested"; items: Item[] }

const STATUS: Record<Req["status"], [string, string]> = {
  ready: ["Ready", "ok-pill"], partly_ready: ["Partly ready", "warn-pill"], requested: ["Requested", "warn-pill"],
};
const fmt = (d: string) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function RequestRecords() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const hotels = useHotels().data?.hotels ?? [];
  const cat = useQuery({ queryKey: ["records-catalog"], queryFn: () => api<Catalog>("/api/records/catalog") });
  const list = useQuery({ queryKey: ["records"], queryFn: () => api<{ requests: Req[] }>("/api/records/requests") });

  const [docType, setDocType] = useState("federal_return");
  const [year, setYear] = useState<number | null>(null);
  const [quarter, setQuarter] = useState("Q4");
  const [hotelIds, setHotelIds] = useState<string[]>([]);
  const [note, setNote] = useState("");
  useEffect(() => { if (cat.data && year === null) setYear(cat.data.defaultYear); }, [cat.data, year]);

  const doc = cat.data?.documents.find((d) => d.key === docType);
  const period = doc?.cadence === "quarterly" ? `${year}-${quarter}` : String(year);
  const send = useMutation({
    mutationFn: () => api<{ id: string; items: Array<{ ready: boolean }> }>("/api/records/requests", { method: "POST", body: { docType, period, hotelIds, note } }),
    onSuccess: () => { setHotelIds([]); setNote(""); qc.invalidateQueries({ queryKey: ["records"] }); },
  });
  const toggle = (id: string) => setHotelIds(hotelIds.includes(id) ? hotelIds.filter((x) => x !== id) : [...hotelIds, id]);
  const focus = params.get("request");

  return (
    <>
      <div className="eyebrow">Owners Module · Request Records</div>
      <h1>Request Records</h1>
      <div className="row" style={{ alignItems: "flex-start", gap: 18, flexWrap: "nowrap" }}>
        <div className="card" style={{ width: 380, flex: "none" }}>
          <div className="field"><label>Document</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)}>
              {(cat.data?.documents ?? []).map((d) => <option key={d.key} value={d.key} disabled={!d.available}>{d.label}{d.available ? "" : ` (${d.note})`}</option>)}
            </select>
          </div>
          <div className="row">
            <div className="field"><label>{doc?.cadence === "quarterly" ? "Year" : "Tax year"}</label>
              <select value={year ?? ""} onChange={(e) => setYear(Number(e.target.value))}>
                {(cat.data?.years ?? []).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {doc?.cadence === "quarterly" && (
              <div className="field"><label>Quarter</label>
                <select value={quarter} onChange={(e) => setQuarter(e.target.value)}>{["Q1", "Q2", "Q3", "Q4"].map((q) => <option key={q}>{q}</option>)}</select>
              </div>
            )}
          </div>
          <div className="field"><label>Hotels</label>
            <div className="checklist">
              <label><input type="checkbox" checked={hotels.length > 0 && hotelIds.length === hotels.length} onChange={(e) => setHotelIds(e.target.checked ? hotels.map((h) => h.id) : [])} /> <strong>All {hotels.length}</strong></label>
              {hotels.map((h) => <label key={h.id}><input type="checkbox" checked={hotelIds.includes(h.id)} onChange={() => toggle(h.id)} /> {h.name}</label>)}
            </div>
          </div>
          <div className="field"><label>Note (optional)</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. for our CPA" /></div>
          {send.error && <p className="error">{(send.error as Error).message}</p>}
          <button className="btn gold" style={{ width: "100%" }} disabled={!hotelIds.length || !year || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Requesting…" : "Request"}
          </button>
          <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
            These documents carry taxpayer IDs, so they're delivered here, not as email attachments. Anything already on file is ready right away;
            the rest is requested from the hotel's accounting team and appears below when it's uploaded.
          </p>
        </div>

        <div className="card" style={{ flex: 1, minWidth: 0 }}>
          <h2>Your recent requests</h2>
          {list.isLoading ? <p className="muted">Loading…</p> : !list.data?.requests.length ? <p className="muted">Nothing requested yet.</p> : (
            list.data.requests.map((r) => (
              <div key={r.id} style={{ borderBottom: "1px solid var(--line)", padding: "10px 0", background: r.id === focus ? "var(--cream)" : undefined }}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <div><strong>{r.label}</strong> · {r.periodLabel} <span className="muted" style={{ fontSize: 12 }}>· requested {fmt(r.createdAt)}{r.note ? ` · ${r.note}` : ""}</span></div>
                  <span className={`pill ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
                </div>
                <table style={{ marginTop: 6 }}><tbody>
                  {r.items.map((i) => (
                    <tr key={i.id}>
                      <td style={{ padding: "4px 6px" }}>{i.hotelName}</td>
                      <td style={{ padding: "4px 6px" }} className="muted">
                        {i.ready ? `${i.fileName} · uploaded ${fmt(i.uploadedAt!)}${i.downloadedAt ? ` · downloaded ${fmt(i.downloadedAt)}` : ""}` : "Waiting for the hotel's accounting team"}
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "right" }}>
                        {i.ready && <a className="btn secondary" style={{ padding: "2px 10px", fontSize: 12 }} href={`/api/records/items/${i.id}/file`}
                          onClick={() => setTimeout(() => qc.invalidateQueries({ queryKey: ["records"] }), 1500)}>Download</a>}
                      </td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
